import React, { useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useInCloseProximityToViewport, calculateIn } from '../../../../utils';
import { exponentialProgressEasing } from '../../../../utils/exponentialEasing';
import useRenderOnlyClientSide from '../../../../utils/useRenderOnlyClientSide';
import { useRequestAnimFrameOnScroll } from '../../../../context/onRequestAnimFrameOnScroll';
import { useLocalSection } from '../LocalSectionProvider';

export function TransitionFadeOnScroll({ children, className = null, style = null }) {
  const localSection = useLocalSection();
  let render = useRenderOnlyClientSide();

  if (!localSection?.sectionRef || !render) return null;
  return (
    <OpacityOnScroll elemRef={localSection.sectionRef} className={className} style={style}>
      {children}
    </OpacityOnScroll>
  );
}

export function OpacityOnScroll({
  elemRef,
  children,
  disableControlPointerEvents = false, // disable manipulation of pointer events
  className = '',
  style = {}, // supports react-spring values
}) {
  let outOfViewport = !useInCloseProximityToViewport(elemRef);
  const { registerCallback } = useRequestAnimFrameOnScroll();

  const [motion, setMotion] = useSpring(() => ({
    opacity: 0,
    visibility: outOfViewport ? 'hidden' : 'visible',
    ...(!disableControlPointerEvents ? { pointerEvents: 'none' } : null),
  }));

  useEffect(() => {
    function getProgress() {
      let rect = elemRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return calculateIn(rect);
    }

    let unregister = registerCallback(() => {
      const progress = getProgress();
      setMotion.start({ opacity: exponentialProgressEasing(progress), immediate: true });
      if (!disableControlPointerEvents) setMotion.start({ pointerEvents: progress > 0.7 ? 'auto' : 'none' });
    });

    return () => unregister();
  }, [disableControlPointerEvents, elemRef, registerCallback, setMotion]);

  useEffect(() => {
    setMotion.start({
      visibility: outOfViewport ? 'hidden' : 'visible',
    });
  }, [outOfViewport, setMotion]);

  return (
    <animated.div style={{ ...motion, ...style, width: '100%' }} className={className}>
      {children}
    </animated.div>
  );
}
